import React, { useCallback, useRef, useState } from 'react';
import { getDefaultSorting, InfiniteScrollTable, Text } from '@ydistri/ds';
import { CalculationStatisticRouteResponse } from '@ydistri/api-sdk';
import {
  ModalDataInfiniteScrollParams,
  useGetStatsRoutesQuery,
} from '../../apiCalculationStatistics';
import { TableProps } from 'antd';
import EditRedistributionButton from '../../../Editation/EditRedistributionButton';
import { useTableRouteColumns } from '../columns/useTableRouteColumns';
import StatisticsModalTableFooter from '../StatisticsModalTableFooter';
import { computeRemSize } from '@ydistri/theme';

interface StatisticsModalTableRouteProps {
  calculationId: number;
  params: ModalDataInfiniteScrollParams;
  setParams: React.Dispatch<React.SetStateAction<ModalDataInfiniteScrollParams>>;
  displayDepartment?: boolean;
}

const StatisticsModalTableRoute: React.FC<StatisticsModalTableRouteProps> = ({
  calculationId,
  params,
  setParams,
  displayDepartment = false,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]); //selected keys need to be controlled for reset
  const selectedRowsRef = useRef<CalculationStatisticRouteResponse[]>([]);

  const columns = useTableRouteColumns(displayDepartment);

  const { data, isFetching } = useGetStatsRoutesQuery({
    ...params,
    tableOrChart: 'table',
    sortings: params.sortings ?? getDefaultSorting(columns),
  });

  // rowSelection object indicates the need for row selection
  const rowSelection: TableProps<CalculationStatisticRouteResponse>['rowSelection'] = {
    type: 'checkbox',
    onChange: (selectedRowKeys: React.Key[], selectedRows: CalculationStatisticRouteResponse[]) => {
      setSelectedRowKeys(selectedRowKeys);
      selectedRowsRef.current = selectedRows;
    },
    selectedRowKeys,
    preserveSelectedRowKeys: false,
  };

  const resetSelection = useCallback(() => {
    setSelectedRowKeys([]);
    selectedRowsRef.current = [];
  }, []);

  return (
    <>
      <InfiniteScrollTable<CalculationStatisticRouteResponse, ModalDataInfiniteScrollParams>
        id="statistics-modal-route-table"
        rowSelection={rowSelection}
        rowKey={r => `${r.sourceStore.id}-${r.targetStore.id}-${r.department?.id}`}
        columns={columns}
        dataSource={data}
        loading={isFetching}
        size="small"
        setParams={setParams}
        height={computeRemSize(400)}
      />

      <StatisticsModalTableFooter key="footer">
        {selectedRowKeys.length > 0 && (
          <>
            <Text>{selectedRowKeys.length} routes selected</Text>
            <EditRedistributionButton
              calculationId={calculationId}
              text="Remove..."
              routes={selectedRowsRef.current}
              useDepartments={displayDepartment}
              onClick={resetSelection}
            />
          </>
        )}
      </StatisticsModalTableFooter>
    </>
  );
};

export default StatisticsModalTableRoute;
